/**
 * OBLI (OptiBay Load Index) - Unified calculation utility
 * 
 * A composite metric indicating day stress level:
 * - 0: No tickets / Shop closed
 * - 1-25: Light Day (green) - Plenty of room for scheduling
 * - 26-60: Moderate Load (yellow) - Balanced workload
 * - 61-85: Heavy Load (orange) - Busy, monitor for delays
 * - 86-100: Overloaded (red) - At or over capacity
 * 
 * The formula is primarily based on capacity utilization with additional
 * penalties for stress factors (waiters, specialty jobs, short-staffing).
 */

export interface OBLIInput {
  /** Total technician capacity in minutes for the day */
  totalCapacity: number;
  /**
   * Base workload minutes for the OBLI utilization calculation.
   *
   * Phase G2B semantic note: the field name is historical. Live
   * operational callers (`OBLIBucket`, `DayboardLoadIndicator`, and the
   * Shop Overview's `ShopUtilizationCard`) now pass the canonical
   * **live projected workload** total — i.e.
   * `sumWorkloadMinutes(getDerivedTicketTime[])` — which includes
   * run-long and bay-blocking pause and correctly excludes held-in-
   * parking / non-bay holds. The penalty weights (waiter +3, specialty
   * +2, short-staff +10/+5) are unchanged and continue to layer on
   * top of whatever base the caller supplies.
   *
   * Historical / forecasting callers (none today) MAY still pass a
   * `predicted_minutes` sum as a "scheduled load" baseline; the
   * formula behaves identically — only the interpretation of the
   * resulting score differs ("scheduled stress" vs "live stress").
   */
  bookedMinutes: number;
  /** Number of active tickets (excluding parking lot, completed, deleted) */
  ticketCount: number;
  /** Number of waiter tickets */
  waiterCount: number;
  /** Number of specialty jobs (ADAS + Alignment) */
  specialtyJobCount: number;
  /** Number of technicians working on this day */
  workingTechCount: number;
  /** Total number of active technicians in shop */
  totalTechCount: number;
  /**
   * Core-100 R4 (CORE-AV-GAP-005): staffing measured in MINUTES. The roster's scheduled productive minutes for the
   * day (absence, time off and breaks already subtracted) and the minutes the same roster works on a normal day. When
   * both are supplied (nominal > 0) the short-staffing stress is scheduled ÷ nominal — a technician working two hours
   * of eight is not "present" — and the head-count ratio is not used. `assembleOBLIDay` supplies both.
   */
  scheduledStaffMinutes?: number;
  nominalStaffMinutes?: number;
  /** Whether the shop is open on this day */
  isShopOpen: boolean;
  /**
   * Whether `totalCapacity` is a MEASUREMENT. False while the roster or the
   * work-role answer is still loading, when either read failed or was
   * refused, or when every technician's hours are unrecorded. Unknown
   * capacity is not zero capacity, and a ratio over it is not a load index —
   * the result is then `unavailable`, never 0 and never 100. Defaults to true
   * for callers that compute capacity from data they already hold.
   */
  capacityKnown?: boolean;
}

/**
 * Which of the distinct situations the score describes. `score` alone cannot
 * say it: 0 is both "closed" and "empty day", and 100 was both "genuinely
 * overloaded" and "we could not read the roster".
 */
export type OBLIState =
  /** Shop closed this day. */
  | 'closed'
  /** Capacity could not be determined (loading / failed / unknown). Not a score. */
  | 'unavailable'
  /** Known capacity is ZERO — nobody is scheduled — yet work is booked. Severe, and real. */
  | 'no_scheduled_capacity'
  /** Known capacity is zero and nothing is booked. */
  | 'no_capacity'
  /** Known capacity, no tickets. */
  | 'empty'
  /** Known capacity, tickets: a measured load. */
  | 'measured';

export interface OBLIResult {
  /** Which situation this is; render `unavailable` as such, never as its score. */
  state: OBLIState;
  /** The OBLI score (0-100). Meaningless (0) when `state` is 'unavailable'. */
  score: number;
  /** Human-readable label */
  label: string;
  /** Detailed description of the day's load */
  description: string;
  /** Capacity utilization percentage */
  utilizationPercent: number;
}

/**
 * Calculate OBLI score using a unified, intuitive formula.
 * 
 * The score is primarily based on capacity utilization (bookedMinutes / totalCapacity)
 * with additional penalties for:
 * - Waiters (+3 points each) - require tighter scheduling
 * - Specialty jobs (+2 points each) - ADAS/Alignment need specific resources
 * - Short-staffing (+10 points if <50% techs available)
 */
export function calculateOBLI(input: OBLIInput): OBLIResult {
  const {
    totalCapacity,
    bookedMinutes,
    ticketCount,
    waiterCount,
    specialtyJobCount,
    workingTechCount,
    totalTechCount,
    scheduledStaffMinutes,
    nominalStaffMinutes,
    isShopOpen,
    capacityKnown = true,
  } = input;

  // Shop closed or no data
  if (!isShopOpen) {
    return {
      state: 'closed',
      score: 0,
      label: 'Closed',
      description: 'Shop is closed on this day.',
      utilizationPercent: 0
    };
  }

  // Capacity is not known: say so. Never a number dressed as a measurement.
  if (!capacityKnown || !Number.isFinite(totalCapacity)) {
    return {
      state: 'unavailable',
      score: 0,
      label: OBLI_CAPACITY_UNAVAILABLE_LABEL,
      description: 'Technician capacity for this day could not be determined, so the load index is not shown.',
      utilizationPercent: 0
    };
  }

  // KNOWN zero capacity — nobody is scheduled to work — but work is booked.
  // That is a real and severe condition, and it stays the maximum score.
  if (totalCapacity === 0) {
    if (ticketCount > 0) {
      return {
        state: 'no_scheduled_capacity',
        score: 100,
        label: 'Overloaded',
        description: 'No technician is scheduled to work this day, but tickets are booked.',
        utilizationPercent: 0
      };
    }
    return {
      state: 'no_capacity',
      score: 0,
      label: 'No Capacity',
      description: 'No technician capacity configured for this day.',
      utilizationPercent: 0
    };
  }

  // No tickets = empty day
  if (ticketCount === 0) {
    return {
      state: 'empty',
      score: 0,
      label: 'Light Day',
      description: 'No tickets scheduled. Great day to add more work.',
      utilizationPercent: 0
    };
  }

  // Base OBLI from capacity utilization
  const utilizationPercent = (bookedMinutes / totalCapacity) * 100;
  let obli = utilizationPercent;

  // Penalty for waiters (+3 per waiter) - they require tighter scheduling
  obli += waiterCount * 3;

  // Penalty for specialty jobs (+2 per specialty) - need specific bays/resources
  obli += specialtyJobCount * 2;

  // Penalty for short-staffing — in productive minutes when known (CORE-AV-GAP-005), else by head count.
  const staffMinutesKnown = Number.isFinite(scheduledStaffMinutes) && Number.isFinite(nominalStaffMinutes)
    && (nominalStaffMinutes as number) > 0;
  if (staffMinutesKnown || totalTechCount > 0) {
    const techRatio = staffMinutesKnown
      ? (scheduledStaffMinutes as number) / (nominalStaffMinutes as number)
      : workingTechCount / totalTechCount;
    if (techRatio < 0.5) {
      obli += 10; // Short-staffed penalty
    } else if (techRatio < 0.75) {
      obli += 5; // Moderately short-staffed
    }
  }

  // Clamp to 0-100 range
  const score = Math.min(100, Math.max(0, Math.round(obli)));

  return {
    state: 'measured',
    score,
    label: getOBLILabel(score),
    description: getOBLIDescription(score),
    utilizationPercent: Math.round(utilizationPercent)
  };
}

export const OBLI_CAPACITY_UNAVAILABLE_LABEL = 'Capacity unavailable';

/** The score as text: a dash, never a number, when capacity is unavailable. */
export function formatOBLIScore(result: Pick<OBLIResult, 'state' | 'score'>): string {
  return result.state === 'unavailable' ? '—' : String(result.score);
}

/**
 * Is the shop's capacity for a day a measurement? All of: the roster has
 * loaded (for THIS shop), the work-role answer is known, and — when no known
 * capacity exists at all — that is not merely because nobody's hours are
 * recorded.
 */
export function isOBLICapacityKnown(input: {
  rosterLoaded: boolean;
  mechanicalRosterKnown: boolean;
  knownCapacityMinutes: number;
  unknownHoursCount: number;
}): boolean {
  if (!input.rosterLoaded || !input.mechanicalRosterKnown) return false;
  if (input.knownCapacityMinutes === 0 && input.unknownHoursCount > 0) return false;
  return true;
}

/**
 * Get human-readable label for OBLI score
 */
export function getOBLILabel(score: number): string {
  if (score === 0) return 'Light Day';
  if (score <= 25) return 'Light Day';
  if (score <= 60) return 'Moderate';
  if (score <= 85) return 'Busy';
  return 'Overloaded';
}

/**
 * Get detailed description for OBLI score
 */
export function getOBLIDescription(score: number): string {
  if (score === 0) return 'No tickets scheduled. Great day to add more work.';
  if (score <= 25) return 'Light workload. Plenty of capacity for additional scheduling.';
  if (score <= 60) return 'Balanced day. Good workflow distribution with some flexibility.';
  if (score <= 85) return 'Busy schedule. Monitor for delays and consider reassignments.';
  return 'At or over capacity. Expect delays, consider parking lot or rescheduling.';
}

/**
 * Get color styling for OBLI score
 */
export function getOBLIColorClasses(score: number): {
  bg: string;
  text: string;
  border: string;
  glow: string;
} {
  if (score === 0) {
    return { 
      bg: 'bg-muted/50', 
      text: 'text-muted-foreground', 
      border: 'border-muted',
      glow: ''
    };
  }
  if (score <= 25) {
    return { 
      bg: 'bg-success/20', 
      text: 'text-success', 
      border: 'border-success/30',
      glow: 'ring-2 ring-success/30'
    };
  }
  if (score <= 60) {
    return { 
      bg: 'bg-warning/20', 
      text: 'text-warning', 
      border: 'border-warning/30',
      glow: 'ring-2 ring-warning/30'
    };
  }
  if (score <= 85) {
    return { 
      bg: 'bg-orange-500/20', 
      text: 'text-orange-600', 
      border: 'border-orange-500/30',
      glow: 'ring-2 ring-orange-500/30'
    };
  }
  return { 
    bg: 'bg-destructive/20', 
    text: 'text-destructive', 
    border: 'border-destructive/30',
    glow: 'ring-2 ring-destructive/30'
  };
}
